// GOLDEN PATH CODE
// This is a Carousel-enabled card component.
// It can be used to display an arbitrarily large list of items.  Only a single row
// of items is displayed, and users can use pagination to see more items in the list.

// Components
import { useEffect, useRef } from 'react';
import {
  Cards,
  CardsProps,
  Header,
  Pagination,
  PaginationProps,
} from '@amzn/awsui-components-react/polaris';
import {
  DISPLAY_ATTENDANCE_INFO,
  DISPLAY_ATTRIBUTES_TYPE,
  DISPLAY_DATE_INFO,
  DISPLAY_SIGNUPS_REMAINING_INFO_WITHOUT_CONDITION,
  DISPLAY_SIGNUPS_REMAINING_INFO_WITH_CONDITION,
} from '../shifts/shiftInfo';
// Hooks
import { useCollection } from '@amzn/awsui-collection-hooks';
import { useLocalizedText } from '../../hooks/localizedText';
import { useSearchParams } from 'react-router-dom';
import { useCarouselDisplayCount } from '../../hooks/shifts/shiftDisplay';
import { useUser } from '../../hooks/user';
// Types
import {
  Event,
  GetAttendancesByQueryParamCommandOutput,
  GetShiftsByEventCommandOutput,
  GetSignupsByQueryParamCommandOutput,
  Shift,
} from '@amzn/red-velvet-api';
// Utils
import { useAllPagesRedVelvetQuery } from '../../hooks/allPagesRedVelvetQuery';
import { useSignupAlerts } from '../../hooks/shifts/shiftSignup';
import { filterBetterShifts } from '../../utils/filterUtils';
import { publishKatalMetric } from '../katalAnalytics';
import { ShiftBody, ShiftFooter, ShiftHeader } from './shiftCard';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import {
  SEARCH_AND_ACTIVITY_SHIFT_TABLE,
  SIGNUP_COHORT_CONTROL,
  SIGNUP_COHORT_TREATMENT,
  SIGNUP_EXPOSURE_METRIC,
  SignupSurface,
} from '../../utils/constants';
import { SignupSuccessAlert } from '../shifts/signupSuccessAlert';

// SHIFT_WIDTH is used when determining how many shifts to display in the paginated carousel
// We recently upgraded it from 300 to 400 so the Did Not Attend & Track Time buttons can fit in the card
export const SHIFT_WIDTH_PIXELS = 400;

type ShiftCarouselProps = {
  carouselTitle?: string;
  paginationType?: 'top' | 'bottom' | 'both';
  event: Event;
  displayAttributes?: DISPLAY_ATTRIBUTES_TYPE[];
  /** Signup surface for the cohort click metric; forwarded to ShiftFooter. */
  source?: SignupSurface;
} & Partial<CardsProps>;

function ShiftPagination(props: PaginationProps) {
  const { t } = useLocalizedText(['translation']);
  return (
    <Pagination
      {...props}
      onChange={(event) => {
        publishKatalMetric('shiftPage', event.detail.currentPageIndex.toString());
        if (props.onChange) props.onChange(event);
      }}
      ariaLabels={{
        nextPageLabel: t('eventList.topPaginationControl.next'),
        previousPageLabel: t('eventList.topPaginationControl.previous'),
        paginationLabel: t('eventList.topPaginationControl.label'),
      }}
    />
  );
}

export function ShiftCarousel(props: ShiftCarouselProps) {
  const shiftsPerPage = useCarouselDisplayCount(SHIFT_WIDTH_PIXELS);
  const {
    event,
    displayAttributes = [DISPLAY_DATE_INFO, DISPLAY_SIGNUPS_REMAINING_INFO_WITHOUT_CONDITION],
  } = props;
  const user = useUser();
  const features = useFeatures();

  // GOLDEN PATH NOTE
  // Whenever possible, components should be responsible for fetching their own data
  // In this case, the carousel needs shifts and signups, so we need to query both
  const shiftsQuery = useAllPagesRedVelvetQuery({
    redVelvetQueryKey: ['getShiftsByEvent', { eventId: event.eventId }],
    converter: (output: GetShiftsByEventCommandOutput) => ({
      items: output.shifts || [],
      next: output.page,
    }),
  });
  const signupQuery = useAllPagesRedVelvetQuery({
    redVelvetQueryKey: ['getSignupsByQueryParam', { eventId: event.eventId, alias: user.userId }],
    converter: (output: GetSignupsByQueryParamCommandOutput) => ({
      items: output.signups || [],
      next: output.page,
    }),
  });
  const attendanceQuery = useAllPagesRedVelvetQuery({
    redVelvetQueryKey: [
      'getAttendancesByQueryParam',
      { eventId: event.eventId, userAlias: user.userId },
    ],
    converter: (output: GetAttendancesByQueryParamCommandOutput) => ({
      items: output.attendances || [],
      next: output.page,
    }),
  });

  const [searchParams] = useSearchParams();

  const alertInfo = useSignupAlerts(event.eventId);
  const { shiftInfo } = alertInfo;

  // GOLDEN PATH NOTE
  // The carousel component should be the source of truth for what children are displayed
  // so that parents can simply pass in the data and get a consistent result
  // every time they use the carousel
  const filteredShifts = filterBetterShifts(
    shiftsQuery.data || [],
    signupQuery.data || [],
    searchParams
  );

  // GOLDEN PATH NOTE
  // The carousel should manage any alerts that need to display for the
  // whole carousel
  const alertContent =
    alertInfo.alert !== undefined ? (
      <SignupSuccessAlert
        event={event}
        alert={alertInfo.alert}
        shiftInfo={shiftInfo}
        onDismiss={alertInfo.onDismiss}
      />
    ) : null;

  const { items, paginationProps } = useCollection(filteredShifts, {
    filtering: {},
    pagination: { pageSize: props.cardsPerRow ? props.cardsPerRow[0].cards : shiftsPerPage },
    sorting: {},
    selection: {},
  });

  // Derived IDENTICALLY to the ToggleSignup click path (actions.tsx) so the exposure denominator and
  // the signup-click numerator share the same cohort bucket.
  const isSearchActivityShiftTableOn = featureIsEnabled(
    SEARCH_AND_ACTIVITY_SHIFT_TABLE,
    features,
    searchParams
  );

  // Denominator mirror of the SIGNUP_COHORT_METRIC ('shifts_signup_cohort') click numerator emitted by
  // ToggleSignup (actions.tsx): one exposure per surface view, keyed with the same `${cohort}:${source}`
  // value, so signup click-through = SIGNUP_COHORT_METRIC clicks / SIGNUP_EXPOSURE_METRIC exposures.
  const exposureEmittedRef = useRef(false);
  const exposedEventIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Reset the once-per-view latch when this carousel instance is reused for a different event, so
    // the new event emits its own exposure instead of staying latched from the previous one — keeping
    // the denominator correct across event switches.
    if (exposedEventIdRef.current !== event.eventId) {
      exposedEventIdRef.current = event.eventId;
      exposureEmittedRef.current = false;
    }
    if (exposureEmittedRef.current) return;
    // Hold the emit until feature flags resolve: useFeatures() is null while loading and
    // featureIsEnabled then reads false, so emitting now buckets a genuine treatment user as control
    // and the latch never corrects it once flags arrive, skewing the A/B denominator.
    if (features === null) return;
    // Hold the emit until the carousel's data has resolved so we can tell a genuinely empty surface
    // from one that is still loading.
    if (!shiftsQuery.isFetched || !signupQuery.isFetched || !attendanceQuery.isFetched) return;
    // Skip empty mounts: a view with no shifts offers nothing to sign up for, so it must not dilute
    // the denominator and understate the click-through rate.
    if (filteredShifts.length === 0) return;
    exposureEmittedRef.current = true;
    const cohort = isSearchActivityShiftTableOn ? SIGNUP_COHORT_TREATMENT : SIGNUP_COHORT_CONTROL;
    publishKatalMetric(SIGNUP_EXPOSURE_METRIC, props.source ? `${cohort}:${props.source}` : cohort);
  }, [
    event.eventId,
    shiftsQuery.isFetched,
    signupQuery.isFetched,
    attendanceQuery.isFetched,
    filteredShifts.length,
    isSearchActivityShiftTableOn,
    features,
    props.source,
  ]);

  if (!shiftsQuery.isFetched || !signupQuery.isFetched || !attendanceQuery.isFetched) return null;
  return (
    <>
      {props.carouselTitle || props.paginationType === 'top' || props.paginationType === 'both' ? (
        // GOLDEN PATH NOTE
        // The Headers are used to ensure that pagination is easily accessible and displays on the right
        // side of the page. However, it should only display if either pagination or title are included
        <Header
          variant="h2"
          actions={
            props.pagination === 'top' || props.pagination === 'both' ? (
              <ShiftPagination {...paginationProps} />
            ) : null
          }
        >
          {props.carouselTitle}
        </Header>
      ) : null}

      {/* This test id is ONLY used to check if the cards are loaded in the snapshot integ tests */}
      {/* because the snapshot tests override Cloudscape rendering so there is no indicator if */}
      {/* items are loaded */}
      <div data-testid="shift-carousel" className="shiftCard">
        <Cards
          // GOLDEN PATH NOTE
          // Use the window observer to determine how many items to display in the carousel by default
          // but allow the parent component to override this for design reasons
          cardsPerRow={props.cardsPerRow || [{ cards: shiftsPerPage }]}
          {...props}
          cardDefinition={{
            header: (shift: Shift) => <ShiftHeader shift={shift} />,
            sections: [
              {
                content: (shift: Shift) => {
                  // Filter displayAttributes based on conditions before passing to ShiftBody
                  const updatedDisplayAttributes = displayAttributes.filter((attribute) => {
                    if (
                      attribute === DISPLAY_SIGNUPS_REMAINING_INFO_WITH_CONDITION &&
                      shift.complete
                    ) {
                      // Remove DISPLAY_SIGNUPS_REMAINING_INFO_WITH_CONDITION if shift is complete
                      return false;
                    }
                    if (attribute === DISPLAY_ATTENDANCE_INFO && !shift.complete) {
                      // Remove DISPLAY_ATTENDANCE_INFO if shift is incomplete
                      return false;
                    }
                    // Keep all other attributes
                    return true;
                  });
                  return (
                    <ShiftBody
                      event={event}
                      shift={shift}
                      attendance={attendanceQuery.data?.find((a) => a.shiftId === shift.shiftId)}
                      displayAttributes={updatedDisplayAttributes}
                    />
                  );
                },
              },
              {
                content: (shift: Shift) => (
                  <ShiftFooter
                    event={event}
                    shift={shift}
                    signup={signupQuery.data?.find((s) => s.shiftId === shift.shiftId)}
                    attendance={attendanceQuery.data?.find((a) => a.shiftId === shift.shiftId)}
                    source={props.source}
                  />
                ),
              },
            ],
          }}
          items={items}
        />
      </div>
      {alertContent}
      {props.paginationType === 'bottom' || props.paginationType === 'both' ? (
        <Header variant="h2" actions={<ShiftPagination {...paginationProps} />} />
      ) : null}
    </>
  );
}
